import React from 'react';
import { Button } from 'antd';
import { CloseOutlined, RightOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import { ContactFollowedBy, ContactHeatPill, ContactTagList } from '../../contacts/components/ContactTags';
import { SparkleIcon } from '../../contacts/components/ContactDrawer';
import { ConversationDisplay, formatActivityPreview, formatThreadDay } from '../types/ConversationDisplay';

interface ConversationContactPanelProps {
  conversation: ConversationDisplay;
  onOpenContact: () => void;
  onClose: () => void;
}

// Side panel of the thread: the essentials of the contact, the full sheet stays in ContactDrawer
const ConversationContactPanel: React.FC<ConversationContactPanelProps> = ({ conversation, onOpenContact, onClose }) => {
  const { contact, lastActivity } = conversation;

  return (
    <aside className="conversations__contact">
      <div className="conversations__contact-head">
        <span>Le contact</span>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} aria-label="Fermer" />
      </div>

      <section className="conversations__contact-section">
        <div className="conversations__contact-identity">
          <span className="contacts__avatar contacts__avatar--large">{conversation.initials}</span>
          <div className="conversations__contact-identity-text">
            <div className="conversations__contact-name">{conversation.fullName}</div>
            <a className="contacts__link" href={`tel:${conversation.formattedPhone.replace(/\s/g, '')}`}>
              {conversation.formattedPhone}
            </a>
            <ContactFollowedBy label={conversation.followedBy} isOwn={conversation.isOwn} />
          </div>
        </div>
        {contact && (
          <button type="button" className="conversations__contact-open" onClick={onOpenContact}>
            Ouvrir la fiche
            <RightOutlined />
          </button>
        )}
      </section>

      {contact && (
        <section className="conversations__contact-section">
          <div className="contacts__section-title">Qualification</div>
          <div className="conversations__contact-field">
            <span className="contacts__info-label">Chaleur</span>
            {contact.heat ? <ContactHeatPill heat={contact.heat} level={contact.heatLevel} /> : '-'}
          </div>
          <div className="conversations__contact-field">
            <span className="contacts__info-label">Typologie</span>
            {contact.typologies.length > 0 ? <ContactTagList tags={contact.typologies} /> : '-'}
          </div>
          <div className="conversations__contact-field">
            <span className="contacts__info-label">Tags personnalisés</span>
            {contact.customTags.length > 0 ? <ContactTagList tags={contact.customTags} /> : '-'}
          </div>
        </section>
      )}

      {/* Confidential contacts get no AI analysis */}
      {contact && !contact.isConfidential && (
        <section className="conversations__contact-section">
          <div className="conversations__contact-title">
            <SparkleIcon />
            Synthèse IA
          </div>
          {contact.summaryMarkdown ? (
            <div className="conversations__contact-text">
              <ReactMarkdown>{contact.summaryMarkdown}</ReactMarkdown>
            </div>
          ) : (
            <p className="contacts__muted">Pas encore de synthèse pour ce contact.</p>
          )}
        </section>
      )}

      <section className="conversations__contact-section">
        <div className="contacts__section-title">Dernier échange</div>
        <div className="contacts__info-label">
          {formatThreadDay(lastActivity.timestamp)} · {dayjs(lastActivity.timestamp).format('HH:mm')}
        </div>
        <div className="conversations__contact-text">{formatActivityPreview(lastActivity)}</div>
      </section>
    </aside>
  );
};

export default ConversationContactPanel;
